/**
 * Unit Stat Helpers for Alberath Combat System
 *
 * Provides color coding and formatting for unit stats based on averages
 * calculated from all 13 main units in combat_system_alberath_v2.7.md
 */
import React from 'react';
import { STAT_ICONS } from '@/utils/statIcons';

// Average stats from all 13 main units (excluding summons)
export const STAT_AVERAGES = {
  HP: 3.77,
  ARM: 0.69,
  WILL: 1.23,
  SPD: 3.85,
  MOV: 3.46,
  AP: 3.31,
  STR: 1.15,
  PSI: 1.23,
  VIS: 5.85
};

/**
 * Get color class for a stat value based on average
 * @param {number} value - The stat value
 * @param {number} average - The average value for this stat
 * @returns {string} - Color class name: 'stat-high', 'stat-avg', or 'stat-low'
 */
export function getStatColorClass(value, average) {
  const threshold = 0.5; // ±0.5 range for "average"

  if (value > average + threshold) {
    return 'stat-high'; // Green - above average
  } else if (value < average - threshold) {
    return 'stat-low'; // Red - below average
  } else {
    return 'stat-avg'; // Yellow/Gold - average
  }
}

/**
 * Get stat color class by stat name
 * @param {string} statName - Name of the stat (HP, ARM, WILL, etc.)
 * @param {number} value - The stat value
 * @returns {string} - Color class name
 */
export function getStatColor(statName, value) {
  const average = STAT_AVERAGES[statName];
  if (!average && average !== 0) {
    return 'stat-avg'; // Default to average if stat not found
  }
  return getStatColorClass(value, average);
}

/**
 * Get tooltip content for a stat showing value context (Low/Average/High)
 * Returns JSX with colored rating
 * @param {string} statName - Name of the stat (HP, ARM, WILL, etc.)
 * @param {number} value - The stat value
 * @returns {JSX.Element} - Tooltip JSX with colored rating
 */
export function getStatTooltip(statName, value) {
  const statFullNames = {
    HP: 'Health Points',
    ARM: 'Armor',
    WILL: 'Willpower',
    SPD: 'Speed',
    MOV: 'Movement',
    AP: 'Action Points',
    STR: 'Strength',
    PSI: 'Psi Power',
    VIS: 'Vision'
  };

  const average = STAT_AVERAGES[statName];
  const fullName = statFullNames[statName] || statName;

  if (average === undefined) {
    return fullName;
  }

  const threshold = 0.5;
  let rating;
  let ratingColor;
  if (value > average + threshold) {
    rating = 'High';
    ratingColor = 'var(--color-green)';
  } else if (value < average - threshold) {
    rating = 'Low';
    ratingColor = 'var(--color-red)';
  } else {
    rating = 'Average';
    ratingColor = 'var(--color-gold)';
  }

  return (
    <span>
      {fullName}: {value} (<span style={{ color: ratingColor, fontWeight: 600 }}>{rating}</span> - Avg: {average.toFixed(1)})
    </span>
  );
}

/**
 * Get stat bar width percentage (0-100%)
 * Normalized to show relative strength within reasonable max values
 * @param {string} statName - Name of the stat
 * @param {number} value - The stat value
 * @returns {number} - Percentage (0-100)
 */
export function getStatBarWidth(statName, value) {
  const maxValues = {
    HP: 7,    // Trent has highest HP
    ARM: 2,   // Guardian/Warlord have highest ARM
    WILL: 3,  // Conjurer has highest WILL
    SPD: 6,   // Sprite has highest SPD
    MOV: 5,   // Several units have 5 MOV
    AP: 5,    // Wisp has highest AP (5)
    STR: 3,   // Warlord has highest STR
    PSI: 2,   // Several units have 2 PSI
    VIS: 6    // Most units have 6 VIS
  };

  const max = maxValues[statName] || 10;
  const percentage = Math.min((value / max) * 100, 100);
  return percentage;
}

/**
 * Check if action is self-targeting by examining targeting.type in movements and apply_effects
 * @param {object} props - Action properties (on_apply)
 * @returns {boolean} - True if action targets self or is AOE from self
 */
function isSelfTargeting(props) {
  // Check movements array (for Move action)
  const movementTargeting = props.movements?.[0]?.targeting;
  if (movementTargeting?.type === 'self') {
    return true;
  }

  // Check apply_effects array (for Fortify, Taunt, buffs)
  const effectTargeting = props.apply_effects?.[0]?.targeting;
  if (effectTargeting?.type === 'self') {
    return true;
  }

  // Check for AOE centered on self (like Taunt)
  // These are self-cast but affect area around caster
  if (effectTargeting?.type === 'area' && effectTargeting?.origin === 'self') {
    return true;
  }
  if (effectTargeting?.type === 'aoe' && effectTargeting?.origin === 'self') {
    return true;
  }

  return false;
}

/**
 * Format action details for display
 * @param {object} action - Action object with properties
 * @param {object} unitTemplate - Optional unit template to extract MOV for movement actions
 * @returns {object} - Formatted action details
 */
export function formatActionDetails(action, unitTemplate = null) {
  // Action properties are nested under 'on_apply' trigger
  const props = action.properties?.on_apply || {};

  return {
    name: action.name,
    pa: action.ap_cost,
    energy: action.energy_cost,
    cooldown: action.cooldown,
    range: (() => {
      // Check if action is self-targeting (movements or apply_effects)
      const isSelf = isSelfTargeting(props);

      if (isSelf) {
        // Self-targeting movement action: show 1-MOV
        if (action.is_movement) {
          if (!unitTemplate) {
            throw new Error(`Movement action "${action.name}" requires unitTemplate but it was not provided`);
          }

          const mov = unitTemplate.max_movement;
          if (!mov) {
            throw new Error(`Movement action "${action.name}" - unitTemplate.max_movement is missing. Available keys: ${Object.keys(unitTemplate).join(', ')}`);
          }

          return `1-${mov}`;
        }

        // Self-targeting non-movement action: show "Self"
        return 'Self';
      }

      // Regular actions use cast range from properties
      const { cast_min_range: min, cast_max_range: max } = props;

      if (max === undefined) {
        throw new Error(`Action "${action.name}" has no cast_max_range and is not self-targeting. Properties: ${JSON.stringify(props)}`);
      }

      if (max === 1) return 'Melee';
      if (min === max) return String(max);
      return `${min}-${max}`;
    })(),
    damage: props.damage_formula || props.damage || null,
    description: action.description || ''
  };
}

/**
 * Format effect details for display
 * @param {object} effect - Effect object
 * @returns {object} - Formatted effect details
 */
export function formatEffectDetails(effect) {
  return {
    name: effect.name,
    duration: effect.duration || 'Permanent',
    type: effect.is_passive ? 'Passive' : (effect.is_buff ? 'Buff' : 'Debuff'),
    description: effect.description || ''
  };
}

/**
 * Inline stat tag for damage tooltips
 * Shows icon + stat name with appropriate color
 */
function StatTag({ statKey }) {
  const config = {
    STR: { icon: STAT_ICONS.strength, label: 'STR', color: 'var(--color-stat-bar-low)' },
    PSI: { icon: STAT_ICONS.psi, label: 'PSI', color: 'var(--color-cyan)' },
    WILL: { icon: STAT_ICONS.will, label: 'WILL', color: '#a855f7' },
    AGI: { icon: STAT_ICONS.agility, label: 'AGI', color: 'var(--color-yellow)' },
  };

  const stat = config[statKey];
  if (!stat) return <span>{statKey}</span>;

  const Icon = stat.icon;

  const tagStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '2px 6px',
    borderRadius: '4px',
    border: `1px solid ${stat.color}`,
    color: stat.color,
    fontWeight: 600,
    fontSize: 'inherit',
  };

  return (
    <span style={tagStyle}>
      <Icon size={12} />
      {stat.label}
    </span>
  );
}

/**
 * Get tooltip JSX for damage tag explaining what it means
 * @param {object} damageTag - Damage tag info { base, scaling, damageType }
 * @returns {JSX.Element|string} - Tooltip JSX explaining damage formula
 */
export function getDamageTagTooltip(damageTag) {
  if (!damageTag) return '';

  if (damageTag.scaling) {
    return (
      <span>
        Deals {damageTag.base} base <StatTag statKey={damageTag.scaling} /> damage + Unit's <StatTag statKey={damageTag.scaling} />
      </span>
    );
  }

  const damageTypeNames = {
    physical: 'Physical',
    psi: 'Psi',
    true: 'True'
  };
  const typeText = damageTypeNames[damageTag.damageType] || damageTag.damageType;

  return `Deals ${damageTag.base} ${typeText} damage`;
}

/**
 * Icon configuration for stats and effects
 * Maps internal keys to display properties
 */
const ICON_CONFIG = {
  // Stats
  str: { icon: 'strength', label: 'STR', color: 'var(--color-damage-str-border)' },
  strength: { icon: 'strength', label: 'STR', color: 'var(--color-damage-str-border)' },
  psi: { icon: 'psi', label: 'PSI', color: 'var(--color-damage-psi-border)' },
  will: { icon: 'will', label: 'WILL', color: 'var(--color-cyan)' },
  agi: { icon: 'agility', label: 'AGI', color: 'var(--color-yellow)' },
  agility: { icon: 'agility', label: 'AGI', color: 'var(--color-yellow)' },
  armor: { icon: 'armor', label: 'ARM', color: 'var(--color-grey)' },
  // Effect types
  buff: { icon: 'will', label: 'Buff', color: 'var(--color-green)' },
  debuff: { icon: 'will', label: 'Debuff', color: 'var(--color-red)' },
  poison: { icon: 'psi', label: 'Poison', color: 'var(--color-green)' },
  healing: { icon: 'hp', label: 'Heal', color: 'var(--color-green-light)' },
};

/**
 * Parse action info for unified display across contexts (Shop, Game, Tooltip)
 * Checks both stat_changes (damage) and apply_effects (buffs/debuffs/poison)
 *
 * @param {object} action - Action object with properties
 * @returns {object|null} - Parsed info { type, iconKey, label, effectSlug, scaling, damageType } or null
 */
export function parseActionTag(action) {
  const onApply = action?.properties?.on_apply;
  if (!onApply) return null;

  // 1. Check for HP damage/healing in stat_changes (highest priority)
  const statChanges = onApply.stat_changes;
  if (statChanges && Array.isArray(statChanges)) {
    const hpDamage = statChanges.find(
      change => change.target_stat === 'hp' && change.operation_direction === 'decrease'
    );
    const hpHealing = statChanges.find(
      change => change.target_stat === 'hp' && change.operation_direction === 'increase'
    );

    const hpChange = hpDamage || hpHealing;
    if (hpChange && hpChange.operations) {
      let base = 0;
      let scaling = null;

      for (const op of hpChange.operations) {
        if (op.type === 'base') {
          base = op.value || 0;
        } else if (op.type === 'add' && op.stat) {
          const statPath = op.stat.split('.');
          const statName = statPath[statPath.length - 1];

          if (statName.includes('psi')) scaling = 'psi';
          else if (statName.includes('strength')) scaling = 'str';
          else if (statName.includes('will')) scaling = 'will';
          else if (statName.includes('agility')) scaling = 'agi';
        }
      }

      if (hpHealing) {
        return {
          type: 'healing',
          iconKey: 'healing',
          label: 'Heal',
          base,
          scaling,
          damageType: null,
          effectSlug: null
        };
      }

      const damageType = hpDamage.damage_type || 'physical';
      // Use scaling stat as icon, or damage type
      const iconKey = scaling || damageType;

      return {
        type: 'damage',
        iconKey,
        label: scaling ? scaling.toUpperCase() : damageType.toUpperCase(),
        base,
        scaling,
        damageType,
        effectSlug: null
      };
    }
  }

  // 2. Check for apply_effects (poison, buffs, debuffs)
  const applyEffects = onApply.apply_effects;
  if (applyEffects && applyEffects.length > 0) {
    const firstEffect = applyEffects[0];
    const effectSlug = firstEffect?.effect_slug;

    if (!effectSlug) return null;

    // Detect effect type from slug
    const slugLower = effectSlug.toLowerCase();

    // Poison effects
    if (slugLower.includes('poison')) {
      return {
        type: 'debuff',
        iconKey: 'poison',
        label: 'Poison',
        base: null,
        scaling: null,
        damageType: null,
        effectSlug
      };
    }

    // Burn effects
    if (slugLower.includes('burn')) {
      return {
        type: 'debuff',
        iconKey: 'str',
        label: 'Burn',
        base: null,
        scaling: null,
        damageType: 'physical',
        effectSlug
      };
    }

    // Stun/root effects
    if (slugLower.includes('stun') || slugLower.includes('root')) {
      return {
        type: 'debuff',
        iconKey: 'debuff',
        label: 'CC',
        base: null,
        scaling: null,
        damageType: null,
        effectSlug
      };
    }

    // Determine buff vs debuff from targeting filters
    const filters = firstEffect?.targeting?.filters;
    const teamFilters = filters?.team || [];
    let targetsAllies = teamFilters.includes('ally') || teamFilters.includes('own');
    let targetsEnemies = teamFilters.includes('enemy');

    // Fallback to can_target_* properties
    if (teamFilters.length === 0) {
      targetsAllies = onApply.can_target_ally || onApply.can_target_self;
      targetsEnemies = onApply.can_target_enemy;
    }

    if (targetsAllies && !targetsEnemies) {
      return {
        type: 'buff',
        iconKey: 'buff',
        label: 'Buff',
        base: null,
        scaling: null,
        damageType: null,
        effectSlug
      };
    }

    if (targetsEnemies && !targetsAllies) {
      return {
        type: 'debuff',
        iconKey: 'debuff',
        label: 'Debuff',
        base: null,
        scaling: null,
        damageType: null,
        effectSlug
      };
    }

    // Default to effect
    return {
      type: 'effect',
      iconKey: 'buff',
      label: 'Effect',
      base: null,
      scaling: null,
      damageType: null,
      effectSlug
    };
  }

  return null;
}

/**
 * Render icon tag based on context
 * @param {string} iconKey - Icon key from ICON_CONFIG (e.g., 'str', 'psi', 'poison')
 * @param {string} label - Label text to display
 * @param {'shop'|'game'|'tooltip'} context - Rendering context
 * @param {object} options - Additional options { size: number }
 * @returns {JSX.Element|null} - Rendered component based on context
 */
export function renderIconTag(iconKey, label, context = 'shop', options = {}) {
  const config = ICON_CONFIG[iconKey];
  if (!config) return <span>{label}</span>;

  const Icon = STAT_ICONS[config.icon] || STAT_ICONS.default;
  const size = options.size || (context === 'game' ? 14 : 12);

  switch (context) {
    case 'game':
      // Game context: just icon, no label (used in ActionsPanel)
      return <Icon size={size} />;

    case 'tooltip':
      // Tooltip context: inline icon + label with color
      return (
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          color: config.color,
        }}>
          <Icon size={size} />
          <span>{label}</span>
        </span>
      );

    case 'shop':
    default:
      // Shop context: bordered tag with icon + label
      return (
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          padding: '2px 6px',
          borderRadius: '4px',
          border: `1px solid ${config.color}`,
          color: config.color,
          fontWeight: 600,
          fontSize: 'inherit',
        }}>
          <Icon size={size} />
          {label}
        </span>
      );
  }
}

/**
 * Parse damage tag from action using formula system
 * Extracts damage info from properties.on_apply.stat_changes[]
 * @deprecated Use parseActionTag for unified parsing
 * @param {object} action - Action object with properties
 * @returns {object|null} - Damage tag info { base, scaling, damageType } or null
 */
export function parseDamageTag(action) {
  const statChanges = action.properties?.on_apply?.stat_changes;

  if (!statChanges || !Array.isArray(statChanges)) {
    return null;
  }

  // Find HP damage entry
  const hpDamage = statChanges.find(
    change => change.target_stat === 'hp' && change.operation_direction === 'decrease'
  );

  if (!hpDamage || !hpDamage.operations) {
    return null;
  }

  // Parse operations to get base value and scaling
  let base = 0;
  let scaling = null;

  for (const op of hpDamage.operations) {
    if (op.type === 'base') {
      base = op.value || 0;
    } else if (op.type === 'add' && op.stat) {
      // Extract stat name from path like 'source.effective_psi'
      const statPath = op.stat.split('.');
      const statName = statPath[statPath.length - 1]; // Get last part

      // Convert effective_psi -> PSI, effective_strength -> STR
      if (statName.includes('psi')) {
        scaling = 'PSI';
      } else if (statName.includes('strength')) {
        scaling = 'STR';
      } else if (statName.includes('will')) {
        scaling = 'WILL';
      } else if (statName.includes('agility')) {
        scaling = 'AGI';
      }
    }
  }

  const damageType = hpDamage.damage_type || 'true';

  return {
    base,
    scaling,
    damageType
  };
}
